/**
 * Discount Service - Priority-Based Discount Management
 *
 * Priority Hierarchy (Highest to Lowest):
 * 1. Customer Discount - If exists, OVERRIDES Category and Product
 * 2. Category Discount - If no Customer discount
 * 3. Product Discount - Only if no Customer and no Category discount
 *
 * Cascading: Multiple levels within each type are applied sequentially
 */

import apiService from 'core/services/apiClient';

const API_PATH = 'web/master/product/discounts';
const CONFIGS_PATH = `${API_PATH}/configs`;
const PRODUCT_VALUES_PATH = `${API_PATH}/product-values`;
const CATEGORY_VALUES_PATH = `${API_PATH}/category-values`;

// Discount Types
export type DiscountType = 'product' | 'category' | 'customer';

// Interfaces
export interface DiscountRule {
  id: string;
  name: string;
  description?: string;
  discountType: DiscountType;
  targetId: string;
  discountPercentage: number;
  priority: number;
  levelNumber: number;
  isActive: boolean;
  organizationId?: number;
  createdAt?: string;
  updatedAt?: string;
}

export interface DiscountRuleGrouped {
  type: DiscountType;
  targetId: string;
  levels: DiscountRule[];
}

export interface CreateDiscountRuleDto {
  name: string;
  description?: string;
  discountType: DiscountType;
  targetId: string;
  discountPercentage: number;
  priority?: number;
  levelNumber?: number;
}

export interface UpdateDiscountRuleDto {
  name?: string;
  description?: string;
  discountPercentage?: number;
  priority?: number;
  levelNumber?: number;
  isActive?: boolean;
}

class DiscountService {
  // ==================== CONFIG ENDPOINTS ====================

  /**
   * Get discount configs by type (PRODUCT or CATEGORY)
   */
  async getDiscountConfigs(type: 'PRODUCT' | 'CATEGORY'): Promise<DiscountRule[]> {
    const response = await apiService.get(CONFIGS_PATH, { params: { type } });
    return response?.data || response || [];
  }

  /**
   * Get all discount rules (product configs)
   */
  async getAllDiscounts(): Promise<DiscountRule[]> {
    return this.getDiscountConfigs('PRODUCT');
  }

  /**
   * Get discounts for a specific product type (config)
   */
  async getProductDiscounts(): Promise<DiscountRule[]> {
    return this.getDiscountConfigs('PRODUCT');
  }

  /**
   * Get discounts for a specific category type (config)
   */
  async getCategoryDiscounts(): Promise<DiscountRule[]> {
    return this.getDiscountConfigs('CATEGORY');
  }

  /**
   * Create a new discount config
   */
  async createDiscount(data: CreateDiscountRuleDto): Promise<DiscountRule> {
    const response = await apiService.post(CONFIGS_PATH, data);
    return response?.data || response;
  }

  /**
   * Update an existing discount config
   */
  async updateDiscount(id: string, data: UpdateDiscountRuleDto): Promise<DiscountRule> {
    const response = await apiService.put(`${CONFIGS_PATH}/${id}`, data);
    return response?.data || response;
  }

  /**
   * Delete a discount config
   */
  async deleteDiscount(id: string): Promise<void> {
    await apiService.delete(`${CONFIGS_PATH}/${id}`);
  }

  /**
   * Toggle discount active status
   */
  async toggleDiscountStatus(id: string, isActive: boolean): Promise<DiscountRule> {
    return this.updateDiscount(id, { isActive });
  }

  // ==================== VALUE ENDPOINTS ====================

  /**
   * Get product discount values for a customer
   */
  async getProductValues(customerId?: string): Promise<any[]> {
    const params = customerId ? { customerId } : {};
    const response = await apiService.get(PRODUCT_VALUES_PATH, { params });
    return response?.data || response || [];
  }

  /**
   * Save product discount values
   */
  async saveProductValues(data: any): Promise<any> {
    const response = await apiService.post(PRODUCT_VALUES_PATH, data);
    return response?.data || response;
  }

  /**
   * Get category discount values for a customer
   */
  async getCategoryValues(customerId?: string): Promise<any[]> {
    const params = customerId ? { customerId } : {};
    const response = await apiService.get(CATEGORY_VALUES_PATH, { params });
    return response?.data || response || [];
  }

  /**
   * Save category discount values
   */
  async saveCategoryValues(data: any): Promise<any> {
    const response = await apiService.post(CATEGORY_VALUES_PATH, data);
    return response?.data || response;
  }

  /**
   * Get discounts for a specific customer (product values)
   */
  async getCustomerDiscounts(customerId: string): Promise<DiscountRule[]> {
    return this.getProductValues(customerId);
  }

  /**
   * Create multiple discount levels at once (for cascading)
   */
  async createDiscountLevels(
    discountType: DiscountType,
    targetId: string,
    levels: Array<{
      name: string;
      discountPercentage: number;
      description?: string;
    }>
  ): Promise<DiscountRule[]> {
    const results: DiscountRule[] = [];

    for (let i = 0; i < levels.length; i++) {
      const level = levels[i];
      const result = await this.createDiscount({
        name: level.name,
        description: level.description,
        discountType,
        targetId,
        discountPercentage: level.discountPercentage,
        priority: i + 1,
        levelNumber: i + 1,
      });
      results.push(result);
    }

    return results;
  }

  /**
   * Calculate effective discount for display (client-side preview)
   * Note: Actual calculation happens on backend via pricing service
   */
  calculateEffectiveDiscount(
    rules: DiscountRule[],
    startPrice: number
  ): {
    finalPrice: number;
    totalDiscount: number;
    effectivePercentage: number;
    breakdown: Array<{ name: string; percentage: number; amount: number; priceAfter: number }>;
  } {
    let currentPrice = startPrice;
    const breakdown: Array<{ name: string; percentage: number; amount: number; priceAfter: number }> = [];

    // Sort by priority (lower first)
    const sortedRules = [...rules]
      .filter(r => r.isActive)
      .sort((a, b) => a.priority - b.priority);

    for (const rule of sortedRules) {
      const discountAmount = (currentPrice * rule.discountPercentage) / 100;
      currentPrice = currentPrice - discountAmount;

      breakdown.push({
        name: rule.name,
        percentage: rule.discountPercentage,
        amount: discountAmount,
        priceAfter: currentPrice,
      });
    }

    const totalDiscount = startPrice - currentPrice;
    const effectivePercentage = startPrice > 0 ? (totalDiscount / startPrice) * 100 : 0;

    return {
      finalPrice: currentPrice,
      totalDiscount,
      effectivePercentage,
      breakdown,
    };
  }
}

export const discountService = new DiscountService();
export default discountService;
